import { useState, useRef, useCallback, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Send, Sparkles, CheckCircle2, XCircle, Timer, Play, SkipForward, ChevronRight, Bell } from "lucide-react";
import { sampleQuestions } from "@/data/sampleData";
import { fuzzyMatch, fmtTime } from "@/lib/quiz";

const TOTAL_SECONDS = 150; // 2:30 per question

interface QuestionResult {
  correct: boolean;
  elapsed: number;
  timeSaved: number;
}

type Phase = "cta" | "question" | "feedback" | "summary";

export const QuizHookSection = () => {
  const [phase, setPhase] = useState<Phase>("cta");
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answer, setAnswer] = useState("");
  const [isCorrect, setIsCorrect] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [results, setResults] = useState<QuestionResult[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);

  const currentQuestion = sampleQuestions[questionIndex];
  const totalQuestions = sampleQuestions.length;
  const isLastQuestion = questionIndex >= totalQuestions - 1;

  /* ── Timer controls ── */
  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    startTimeRef.current = Date.now();
    setElapsed(0);
    timerRef.current = setInterval(() => {
      const sec = Math.floor((Date.now() - startTimeRef.current) / 1000);
      if (sec >= TOTAL_SECONDS) {
        setElapsed(TOTAL_SECONDS);
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = null;
        setIsCorrect(false);
        setResults((prev) => [...prev, { correct: false, elapsed: TOTAL_SECONDS, timeSaved: TOTAL_SECONDS }]);
        setPhase("feedback");
      } else {
        setElapsed(sec);
      }
    }, 200);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    const sec = Math.min(
      Math.floor((Date.now() - startTimeRef.current) / 1000),
      TOTAL_SECONDS
    );
    setElapsed(sec);
    return sec;
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const remaining = TOTAL_SECONDS - elapsed;
  const netTime = elapsed - (isCorrect ? TOTAL_SECONDS : 0);

  /* ── Computed totals ── */
  const totalTimeSaved = results.reduce((sum, r) => sum + r.timeSaved, 0);
  const totalCorrect = results.reduce((sum, r) => sum + (r.correct ? 1 : 0), 0);

  /* ── Start the quiz ── */
  const handleStart = () => {
    setQuestionIndex(0);
    setResults([]);
    setPhase("question");
    setAnswer("");
    startTimer();
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  /* ── Check answer ── */
  const checkAnswer = () => {
    const finalElapsed = stopTimer();
    const correct = fuzzyMatch(answer, currentQuestion.answer);
    // Net time impact: thinking_time - 2:30*(correct?1:0)
    // Negative = saved time, Positive = wasted time
    const net = finalElapsed - (correct ? TOTAL_SECONDS : 0);
    setIsCorrect(correct);
    setElapsed(finalElapsed);
    setResults((prev) => [...prev, { correct, elapsed: finalElapsed, timeSaved: net }]);
    setPhase("feedback");
  };

  /* ── Next question ── */
  const handleNext = () => {
    if (isLastQuestion) {
      setPhase("summary");
    } else {
      setQuestionIndex((i) => i + 1);
      setAnswer("");
      setPhase("question");
      startTimer();
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  /* ── Skip to results ── */
  const handleSkip = () => {
    const finalElapsed = stopTimer();
    // Skipped: wasted thinking time, no bonus
    setResults((prev) => [...prev, { correct: false, elapsed: finalElapsed, timeSaved: finalElapsed }]);
    if (isLastQuestion) {
      setPhase("summary");
    } else {
      setQuestionIndex((i) => i + 1);
      setAnswer("");
      setPhase("question");
      startTimer();
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  /* ── Skip all remaining to summary ── */
  const handleSkipToResults = () => {
    const finalElapsed = stopTimer();
    // Current question: wasted thinking time so far
    const remaining_qs: QuestionResult[] = [
      { correct: false, elapsed: finalElapsed, timeSaved: finalElapsed }
    ];
    // Remaining questions: instant skip, 0 impact
    for (let i = questionIndex + 1; i < totalQuestions; i++) {
      remaining_qs.push({ correct: false, elapsed: 0, timeSaved: 0 });
    }
    setResults((prev) => [...prev, ...remaining_qs]);
    setQuestionIndex(totalQuestions - 1);
    setPhase("summary");
  };

  /* ── Scroll to email form in NextEventSection ── */
  const scrollToNotifyForm = () => {
    const el = document.getElementById("notify-form");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <section className="py-16 md:py-24 relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-muted/30 to-transparent" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[hsl(237,72%,54%,0.04)] dark:bg-[hsl(220,15%,20%,0.3)] rounded-full blur-[100px]" />

      <div className="relative container mx-auto px-5 md:px-8">
        {/* Section header */}
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-xs font-bold uppercase tracking-widest text-brand-orange mb-2">
            Try it now
          </div>
          <h2 className="text-2xl md:text-4xl font-black tracking-tight text-foreground">
            Can you crack these?
          </h2>
          <p className="mt-3 text-muted-foreground text-sm md:text-base">
            These are the kind of questions you'd answer mid-run. No Googling!
          </p>
        </div>

        {/* Card */}
        <div className="max-w-xl mx-auto">
          <div className="rounded-2xl border border-border bg-card shadow-lg overflow-hidden" aria-live="polite">
            {/* Phase: CTA — "Wanna try?" */}
            {phase === "cta" && (
              <div className="p-8 md:p-12 text-center animate-in fade-in duration-300">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 dark:bg-primary/20 mb-5">
                  <Timer className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-black text-foreground mb-2">
                  Think you're quick?
                </h3>
                <p className="text-sm text-muted-foreground mb-6">
                  {totalQuestions} questions. Each correct answer saves you 2:30 on the run. See how fast you can crack them.
                </p>
                <Button
                  onClick={handleStart}
                  size="lg"
                  className="bg-brand-orange hover:bg-[hsl(30_90%_50%)] text-background font-bold shadow-lg shadow-orange-500/20 px-8"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Wanna try?
                </Button>
              </div>
            )}

            {/* Question + timer — visible during question and feedback */}
            {(phase === "question" || phase === "feedback") && (
              <>
                {/* Question counter + timer bar */}
                <div className="px-6 pt-5 md:px-8 md:pt-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {/* Question dots */}
                      {sampleQuestions.map((_, i) => (
                        <div
                          key={i}
                          className={`w-2.5 h-2.5 rounded-full transition-colors ${
                            i < questionIndex
                              ? results[i]?.correct
                                ? "bg-green-500"
                                : "bg-red-400"
                              : i === questionIndex
                                ? "bg-primary"
                                : "bg-muted-foreground/20"
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-xs font-medium text-muted-foreground">
                      {questionIndex + 1} / {totalQuestions}
                    </span>
                  </div>

                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Timer className={`h-4 w-4 ${phase === "question" ? "text-primary animate-pulse" : "text-muted-foreground"}`} />
                      <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                        {phase === "question" ? "Time remaining" : "Time taken"}
                      </span>
                    </div>
                    <span className={`font-mono font-black text-lg ${
                      phase === "question"
                        ? remaining <= 30
                          ? "text-red-500"
                          : "text-foreground"
                        : "text-primary"
                    }`}>
                      {phase === "question" ? fmtTime(remaining) : fmtTime(elapsed)}
                    </span>
                  </div>
                  {/* Progress bar */}
                  <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${
                        phase === "question"
                          ? remaining <= 30
                            ? "bg-red-500"
                            : "bg-primary"
                          : "bg-primary"
                      }`}
                      style={{ width: `${(phase === "question" ? (TOTAL_SECONDS - elapsed) / TOTAL_SECONDS : elapsed / TOTAL_SECONDS) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Question text */}
                <div className="p-6 md:p-8 pt-4 md:pt-5">
                  <p className="text-sm md:text-base leading-relaxed text-foreground font-medium whitespace-pre-line">
                    {currentQuestion.question}
                  </p>
                </div>
              </>
            )}

            {/* Phase: Answer input */}
            {phase === "question" && (
              <div className="px-6 pb-6 md:px-8 md:pb-8">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (answer.trim()) checkAnswer();
                  }}
                  className="flex gap-3"
                >
                  <Input
                    ref={inputRef}
                    placeholder="Your answer…"
                    aria-label="Your answer"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    className="flex-1"
                    autoFocus
                  />
                  <Button
                    type="submit"
                    disabled={!answer.trim()}
                    aria-label="Submit answer"
                    className="bg-primary/80 hover:bg-primary/70 text-white shadow-sm"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
                <div className="mt-3 flex justify-between items-center">
                  <button
                    type="button"
                    onClick={handleSkip}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                  >
                    <SkipForward className="h-3 w-3" />
                    Skip question
                  </button>
                  <button
                    type="button"
                    onClick={handleSkipToResults}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Skip to results →
                  </button>
                </div>
              </div>
            )}

            {/* Phase: Feedback */}
            {phase === "feedback" && (
              <div className="px-6 pb-6 md:px-8 md:pb-8 space-y-5 animate-in fade-in slide-in-from-bottom-2 duration-300">
                {isCorrect ? (
                  <div className="rounded-xl bg-green-50 dark:bg-green-950/30 border border-green-200/50 dark:border-green-800/30 p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-foreground">
                          🎉 Nice one! You answered in {fmtTime(elapsed)}.
                        </p>
                        <p className="text-sm font-mono font-bold text-green-600 dark:text-green-400 mt-1">
                          −{fmtTime(Math.abs(netTime))} from your running time!
                        </p>
                        {currentQuestion.comment && (
                          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                            {currentQuestion.comment}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200/50 dark:border-amber-800/30 p-4">
                    <div className="flex items-start gap-3">
                      <XCircle className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-foreground">
                          The answer is{" "}
                          <span className="text-primary">
                            {currentQuestion.answer}
                          </span>
                          .
                        </p>
                        {currentQuestion.comment && (
                          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                            {currentQuestion.comment}
                          </p>
                        )}
                        <p className="text-sm font-mono font-bold text-amber-600 dark:text-amber-400 mt-2">
                          +{fmtTime(elapsed)} thinking time. A correct answer would have been −{fmtTime(TOTAL_SECONDS - elapsed)}.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  {!isLastQuestion ? (
                    <>
                      <Button
                        onClick={handleNext}
                        className="flex-1 bg-brand-orange hover:bg-[hsl(30_90%_50%)] text-background font-bold shadow-lg shadow-orange-500/20"
                      >
                        Next question
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={handleSkipToResults}
                        className="text-muted-foreground"
                      >
                        Skip to results
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={handleNext}
                      className="flex-1 bg-brand-orange hover:bg-[hsl(30_90%_50%)] text-background font-bold shadow-lg shadow-orange-500/20"
                    >
                      See your results
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Phase: Summary — total results across all questions */}
            {phase === "summary" && (
              <div className="p-6 md:p-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 dark:bg-primary/20 mb-4">
                    <Sparkles className="h-8 w-8 text-brand-orange" />
                  </div>
                  <h3 className="text-xl font-black text-foreground">Your Results</h3>
                </div>

                {/* Results grid */}
                <div className="space-y-2 mb-6">
                  {results.map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-sm py-2 px-3 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-2">
                        {r.correct ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-400" />
                        )}
                        <span className="text-foreground font-medium">Q{i + 1}</span>
                        <span className="text-muted-foreground">
                          {sampleQuestions[i]?.answer}
                        </span>
                      </div>
                      <span className={`font-mono font-bold ${r.timeSaved < 0 ? "text-green-600 dark:text-green-400" : r.timeSaved > 0 ? "text-red-500 dark:text-red-400" : "text-muted-foreground"}`}>
                        {r.timeSaved < 0 ? `−${fmtTime(Math.abs(r.timeSaved))}` : r.timeSaved > 0 ? `+${fmtTime(r.timeSaved)}` : "0:00"}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Total */}
                <div className="border-t border-border pt-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-bold text-foreground">
                        {totalCorrect}/{totalQuestions} correct
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {totalTimeSaved < 0
                          ? `That's ${fmtTime(Math.abs(totalTimeSaved))} saved from your running time!`
                          : totalTimeSaved > 0
                          ? `You lost ${fmtTime(totalTimeSaved)} in thinking time!`
                          : "In the real event you'd get 10 questions — plenty of chances!"}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-mono font-black text-2xl ${totalTimeSaved < 0 ? "text-green-600 dark:text-green-400" : totalTimeSaved > 0 ? "text-red-500 dark:text-red-400" : "text-[hsl(30_90%_50%)]"}`}>
                        {totalTimeSaved < 0 ? `−${fmtTime(Math.abs(totalTimeSaved))}` : totalTimeSaved > 0 ? `+${fmtTime(totalTimeSaved)}` : "0:00"}
                      </div>
                      <div className="text-xs text-muted-foreground">net time</div>
                    </div>
                  </div>
                </div>

                <div className="pt-2">
                  <p className="text-sm font-semibold text-foreground mb-3">
                    <Sparkles className="inline h-4 w-4 text-brand-orange mr-1 -mt-0.5" />
                    Want to try the real thing?
                  </p>
                  <Button
                    onClick={scrollToNotifyForm}
                    className="w-full bg-brand-orange hover:bg-[hsl(30_90%_50%)] text-background font-bold shadow-lg shadow-orange-500/20"
                  >
                    <Bell className="h-4 w-4 mr-2" />
                    Notify me
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
